/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-visit-form/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { UseBoolean } from 'react-hanger';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import {
    initialErrorVisitForm,
    initialErrorVisitFormInterface,
    initialStateVisitForm,
    initialStateVisitFormInterface,
} from '@components/admin/add-visit-form/add-visit-form.constants';
import ForecastEndDateField from '@components/admin/forecast-end-date-field';
import LeadVisitIdField from '@components/admin/lead-visit-id-field';
import CopyButton from '@components/core/copy-button';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import HeadingActions from '@components/core/heading-actions';
import Loading from '@components/core/loading';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from 'hooks';
import {
    StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit,
    StudySubjectVisitFields,
} from 'queries/generated/fragments.generated';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import { StudyTimeUnit } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { createArrayFromEnum } from 'utils/generator';
import NotificationService from 'utils/notification';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import CharactersRemaining from 'views/components/core/characters-remaining';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import { getLeadVisitField } from '@utils/study-data.service';
import './styles.scss';

interface AddVisitFormInterface {
    visits: StudySubjectVisitFields[] | null;
    currentVisit?: StudySubjectVisitFields | null;
    currentScheduleId: string | null;
    studyId: string;
    showNewVisit?: UseBoolean;
    formChanged: UseBoolean;
    close?: (action?: string) => void;
    canEdit: boolean;
}

const AddVisitForm = ({
    visits,
    currentVisit,
    currentScheduleId,
    studyId,
    showNewVisit,
    formChanged,
    close,
    canEdit,
}: AddVisitFormInterface) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();
    const user = useAuthorization();

    const [saveVisit, { loading }] = useMutation(SAVE_STUDY_VISIT_SCHEDULE_VISIT);
    const disabledSave = useBoolean(true);
    const [state, setState] = useState<initialStateVisitFormInterface>({ ...initialStateVisitForm });
    const [errors, setErrors] = useState<initialErrorVisitFormInterface>({ ...initialErrorVisitForm });
    const [unitOptions, setUnitOptions] = useState<SelectWithSearchDataArrayInterface[] | []>([]);
    const { endDateUsage } = PanelConstants;
    const [initialVisit, setInitialVisit] = useState<initialStateVisitFormInterface | null>({
        ...initialStateVisitForm,
    });

    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);

    const onResponse = (action?: string) => {
        if (action !== 'error') {
            setErrors({ ...initialErrorVisitForm });
        }
        if (action === 'error') NotificationService.showNotification(t('errorMessage'));
        else {
            if (action === 'success') {
                NotificationService.showNotification(t('admin:successfulCreateVisit'));
                showNewVisit?.setFalse();
            }
            if (action === 'edit')
                NotificationService.showNotification(t('admin:successfulEditVisit', { id: currentVisit?.recordId }));
        }
        disabledSave.setTrue();
    };

    useEffect(() => {
        const array = createArrayFromEnum(StudyTimeUnit, '_UNIT_VISIT', '');
        const index = array.findIndex((item: any) => item.id === StudyTimeUnit.NONE);
        array.splice(index, 1);
        setUnitOptions(array);
    }, []);

    const isLeadVisitId = state.leadVisitId !== Constants.ObjectId.Empty && !!state.leadVisitId?.length;
    const isSaveButtonDisabled =
        Object.keys(errors).filter((item: string) => errors[item]).length > 0 ||
        disabledSave.value ||
        !(canEdit || isSysAdmin);

    useEffect(() => {
        if (currentVisit) {
            const forecastEndDateField =
                currentVisit.forecastEndDateField?.split('|').map((item: string) => item.trim()) ?? [];
            const forecastEndDate = localState?.fields.filter(
                (item: any) => forecastEndDateField.indexOf(item.key) >= 0,
            );

            const currentVisitValues = {
                id: currentVisit.id,
                configId: currentVisit.configId,
                active: currentVisit.active,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId:
                    currentVisit.leadVisitId === Constants.ObjectId.Empty
                        ? ''
                        : getLeadVisitField(visits, currentVisit.leadVisitId, 'visitId', 'id'),
                leadPeriod: currentVisit.leadPeriod || 0,
                leadUnit: currentVisit.leadUnit === 'NONE' ? '' : currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod || 0,
                maxWinPeriod: currentVisit.maxWinPeriod || 0,
                minWinUnit: currentVisit.minWinUnit === 'NONE' ? '' : currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences || 0,
                eventIds: currentVisit.eventIds,
                forecastEndDate,
                endDateUsage: currentVisit.isEndDateEarliest ? 'EARLIEST' : 'LATEST',
                recordId: currentVisit.recordId,
                sequenceNumber: currentVisit.sequenceNumber,
            };

            setState({ ...currentVisitValues } as initialStateVisitFormInterface);
            setInitialVisit({ ...currentVisitValues } as initialStateVisitFormInterface);
        } else setState({ ...initialStateVisitForm });
    }, [currentVisit]);

    useEffect(() => {
        // delete required leadPeriod if leadVisitId empty
        if (state.leadVisitId?.length)
            setErrors({
                ...errors,
                leadPeriod: null,
                minWinPeriod: null,
                maxWinPeriod: null,
                leadUnit: '',
                minWinUnit: '',
                endDateUsage: '',
                forecastEndDate: '',
            });
    }, [state.leadVisitId]);

    const onCheckboxChange = (fieldName: 'active') => () => {
        disabledSave.setFalse();
        formChanged.setTrue();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        if (field === 'configId') {
            const configIdError = checkConfigIdErrors(state, visits, value);
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string, isNumber?: boolean) => (event: any) => {
        const { value } = event.target;

        if (isNumber && +value < 0) return;

        if (isNumber && value.length === 7) return;

        let length = 0;

        if (name === 'visitId' || name === 'leadVisitId') length = 100;

        if (name === 'type' || name === 'visitName') length = 60;

        if (name === 'configId') length = 24;

        disabledSave.setFalse();
        formChanged.setTrue();

        setState({
            ...state,
            [name]: value,
            ...(name === 'leadVisitId' &&
                value === '' && {
                    leadPeriod: 0,
                    leadUnit: '',
                    minWinPeriod: 0,
                    maxWinPeriod: 0,
                    minWinUnit: '',
                    endDateUsage: '',
                    forecastEndDate: [],
                }),
        });

        validate(value, name, length);
    };

    const checkIfVisitIdExist = () => {
        const foundVisitId = visits?.find(
            (visit: StudyVisitScheduleFields_StudySubjectVisit | null) => state.leadVisitId === visit?.visitId,
        );
        if (foundVisitId) return true;

        if ((state.leadVisitId === '' || state.leadVisitId === Constants.ObjectId.Empty) && foundVisitId === undefined)
            return true;

        if (
            currentVisit &&
            currentVisit.leadVisitId === Constants.ObjectId.Empty &&
            !state.leadVisitId &&
            !foundVisitId
        )
            return true;

        disabledSave.setTrue();
        NotificationService.showNotification(t('admin:visitWithVisitIdNotFound'));
        return false;
    };

    const validationOnSubmit = (submitData: any) => {
        const isVisitIdExist = checkIfNameExist(
            { ...submitData.visit, visitId: state.visitId.trim(), id: submitData?.visit?.id },
            visits,
            'visitId',
        );
        const isVisitNameExist = checkIfNameExist(
            { ...submitData.visit, visitName: state.visitName.trim(), id: submitData?.visit?.id },
            visits,
            'visitName',
        );
        const visitIdError = isVisitIdExist ? { visitId: t('admin:visitIdAlreadyExists') } : { visitId: null };
        const visitNameError = isVisitNameExist
            ? { visitName: t('admin:visitNameAlreadyExists') }
            : { visitName: null };
        const leadTimeError = { leadPeriod: null } as any;
        let minOccurencesError = { minOccurrences: null } as any;
        let minWinPeriodError = { minWinPeriod: null } as any;
        let maxWinPeriodError = { maxWinPeriod: null } as any;

        if (state.leadVisitId !== Constants.ObjectId.Empty && state.leadVisitId?.length) {
            if (+state.minOccurrences === 0)
                minOccurencesError = { minOccurrences: t('admin:NumberOfOccurencesMustBeBiggerThanNull') };
            if (+state.minWinPeriod < 0)
                minWinPeriodError = { minWinPeriod: t('admin:MinWinTimeMustBeBiggerThanNull') };
            if (+state.maxWinPeriod < 0)
                maxWinPeriodError = { maxWinPeriod: t('admin:MaxWinTimeMustBeBiggerThanNull') };
        }

        if (
            visitIdError.visitId ||
            visitNameError.visitName ||
            leadTimeError.leadPeriod ||
            minOccurencesError.minOccurrences ||
            minWinPeriodError.minWinPeriod ||
            maxWinPeriodError.maxWinPeriod
        ) {
            disabledSave.setTrue();
            setErrors({
                ...errors,
                ...visitIdError,
                ...visitNameError,
                ...leadTimeError,
                ...minOccurencesError,
                ...minWinPeriodError,
                ...maxWinPeriodError,
            });

            return true;
        }

        return false;
    };

    const submit = () => {
        disabledSave.setTrue();
        const requiredFields = ['type', 'visitId', 'visitName', 'minOccurrences'];

        if (currentVisit) {
            requiredFields.push('configId');
            const configIdError = checkConfigIdErrors(state, visits);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }

        if (state.leadVisitId !== Constants.ObjectId.Empty && state.leadVisitId?.length) {
            requiredFields.push(
                'leadPeriod',
                'minWinPeriod',
                'maxWinPeriod',
                'leadUnit',
                'minWinUnit',
                'forecastEndDate',
                'endDateUsage',
            );
        }

        const validateState = {
            // need for validation in number fields where need to hide zero value
            ...state,
            minOccurrences: state.minOccurrences === 0 ? '' : state.minOccurrences,
            leadPeriod: state.leadPeriod === 0 ? '' : state.leadPeriod,
            minWinPeriod: state.minWinPeriod === 0 ? '' : state.minWinPeriod,
            maxWinPeriod: state.maxWinPeriod === 0 ? '' : state.maxWinPeriod,
        };

        if (
            checkRequiredFields({ fields: requiredFields, state: validateState, setErrors, errors }) &&
            checkIfVisitIdExist()
        ) {
            const submitData = {
                studyId,
                visitScheduleId: currentScheduleId,
                visit: {
                    id: currentVisit?.id ?? null,
                    configId: state.configId,
                    active: state.active,
                    type: state.type,
                    visitId: state.visitId,
                    visitName: state.visitName,
                    leadVisitId:
                        getLeadVisitField(visits, state.leadVisitId, 'id', 'visitId') || Constants.ObjectId.Empty,
                    leadPeriod: +state.leadPeriod || null,
                    leadUnit: state.leadUnit !== '' ? state.leadUnit : 'NONE',
                    minWinPeriod: +state.minWinPeriod || null,
                    maxWinPeriod: +state.maxWinPeriod || null,
                    minWinUnit: state.minWinUnit !== '' ? state.minWinUnit : 'NONE',
                    minOccurrences: +state.minOccurrences,
                    eventIds: state.eventIds,
                    forecastEndDateField: state.forecastEndDate.map((item: any) => item.id || item.key).join(' | '),
                    isEndDateEarliest: state.endDateUsage === 'EARLIEST',
                    sequenceNumber: state.sequenceNumber,
                },
            };

            if (validationOnSubmit(submitData)) return;

            saveVisit({
                variables: {
                    ...submitData,
                },
                refetchQueries: [
                    {
                        query: GET_STUDY_VISIT_SCHEDULES,
                        variables: {
                            studyId,
                        },
                    },
                ],
            })
                .then(() => {
                    onResponse(currentVisit ? 'edit' : 'success');
                    setInitialVisit({ ...state });
                    formChanged.setFalse();
                })
                .catch(() => {
                    onResponse('error');
                });
        }
    };

    return (
        <div className={cn('visit-form', currentVisit ? 'edit' : 'add')}>
            {loading || (!currentVisit && !showNewVisit?.value) ? (
                <Loading />
            ) : (
                <>
                    <div className="visit-panel-header">
                        {!currentVisit ? (
                            <>
                                {t('admin:addVisit')}
                                <div className="buttons-wrapper">
                                    <HeadingActions
                                        items={[
                                            {
                                                element: (
                                                    <DarkTooltip title={t('common:save')} placement="top" arrow>
                                                        <FontAwesomeIcon
                                                            icon={[isSaveButtonDisabled ? 'fal' : 'far', 'floppy-disk']}
                                                            size="xl"
                                                        />
                                                    </DarkTooltip>
                                                ),
                                                action: () => submit(),
                                                disabled: isSaveButtonDisabled,
                                                'data-testid': 'save-button',
                                            },
                                        ]}
                                    />
                                    {!!visits?.length && (
                                        <FontAwesomeIcon
                                            icon={['fal', 'times']}
                                            className="fa-lg close-icon-cv"
                                            onClick={() => {
                                                if (close) close();
                                            }}
                                            data-testid="close-dialog"
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="title">
                                    {state.visitName} - {state.recordId}
                                </div>
                                <HeadingActions
                                    items={[
                                        {
                                            element: (
                                                <DarkTooltip title={t('common:undo')} placement="top" arrow>
                                                    <FontAwesomeIcon icon={['fal', 'undo']} size="xl" />
                                                </DarkTooltip>
                                            ),
                                            action: () => {
                                                formChanged.setFalse();
                                                disabledSave.setTrue();
                                                setErrors(initialErrorVisitForm);
                                                setState({ ...initialVisit } as initialStateVisitFormInterface);
                                            },
                                            disabled: !(canEdit || isSysAdmin),
                                            'data-testid': 'undo-button',
                                        },
                                        {
                                            element: (
                                                <DarkTooltip title={t('common:save')} placement="top" arrow>
                                                    <FontAwesomeIcon
                                                        icon={[isSaveButtonDisabled ? 'fal' : 'far', 'floppy-disk']}
                                                        size="xl"
                                                    />
                                                </DarkTooltip>
                                            ),
                                            action: () => submit(),
                                            disabled: isSaveButtonDisabled,
                                            'data-testid': 'save-button',
                                        },
                                    ]}
                                />
                            </>
                        )}
                    </div>
                    <div className="visit-form-wrapper">
                        <div className="checkboxes-wrapper">
                            <div className="left-part mb-15">
                                <Checkbox
                                    rounded="none"
                                    id="add-visit-active-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.active}
                                    onChange={onCheckboxChange('active')}
                                    label={t('common:active')}
                                    disabled={!canEdit}
                                    labelClassName={cn({ 'checkbox-label-disabled': !canEdit })}
                                    data-testid="add-visit-active-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mb mr">
                                <TextField
                                    label={`${t('admin:visitType')}*`}
                                    id="add-visit-visit-type"
                                    fullWidth
                                    value={state.type}
                                    onChange={onChange('type')}
                                    placeholder={t('admin:enterVisitTypeLabel')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-visit-dialog-name"
                                            length={state.type?.length}
                                            max={60}
                                            error={errors.type}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.type}
                                    disabled={!canEdit}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type mb">
                                <TextField
                                    label={`${t('subject:visitId')}*`}
                                    id="add-visit-id"
                                    fullWidth
                                    value={state.visitId}
                                    onChange={onChange('visitId')}
                                    placeholder={t('admin:enterVisitID')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-visit-id-help-text"
                                            length={state.visitId?.length}
                                            max={100}
                                            error={errors.visitId}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.visitId}
                                    disabled={!canEdit}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mb mr">
                                <TextField
                                    label={`${t('subject:visitName')}*`}
                                    id="add-visit-name"
                                    fullWidth
                                    value={state.visitName}
                                    onChange={onChange('visitName')}
                                    placeholder={t('admin:enterVisitName')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-visit-name-help-text"
                                            length={state.visitName?.length}
                                            max={60}
                                            error={errors.visitName}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.visitName}
                                    disabled={!canEdit}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type mb">
                                <TextField
                                    label={`${t('admin:numberOccurrencesGridLabel')}*`}
                                    id="add-visit-occurrences"
                                    type="number"
                                    value={state.minOccurrences || '1'}
                                    onKeyDown={onChange('minOccurrences', true)}
                                    onChange={onChange('minOccurrences', true)}
                                    placeholder={t('admin:enterNumberOccurrences')}
                                    helperText={
                                        <CharactersRemaining
                                            id="visit-minOccurrences-help-text"
                                            error={errors.minOccurrences}
                                            customText={t('common:enterNumericValue')}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.minOccurrences}
                                    disabled={!canEdit}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mb">
                                <LeadVisitIdField
                                    visits={visits}
                                    onChange={onChange}
                                    currentVisit={currentVisit}
                                    currentState={state.leadVisitId}
                                    error={errors.leadVisitId}
                                    disabled={!canEdit}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr mb">
                                <TextField
                                    label={`${t('admin:leadTime')}${isLeadVisitId ? '*' : ''}`}
                                    id="add-lead-time"
                                    fullWidth
                                    type="number"
                                    disabled={!isLeadVisitId || !canEdit}
                                    value={state.leadPeriod || ''}
                                    onKeyDown={onChange('leadPeriod', true)}
                                    onChange={onChange('leadPeriod', true)}
                                    placeholder={t('admin:enterLeadTime')}
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.leadPeriod}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-lead-time-help-text"
                                            error={errors.leadPeriod}
                                            customText={t('common:enterNumericValue')}
                                        />
                                    }
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type mb">
                                <SelectWithCategories
                                    showWarning={false}
                                    fieldSelect="leadUnit"
                                    onChange={onChange}
                                    label={`${t('admin:leadUnit')}${isLeadVisitId ? '*' : ''}`}
                                    disabled={!isLeadVisitId || !canEdit}
                                    filterable={false}
                                    labelField="leadUnit"
                                    dataArray={unitOptions}
                                    customId="add-select-lead-unit"
                                    currentState={state.leadUnit}
                                    error={errors.leadUnit}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr mb">
                                <TextField
                                    label={`${t('admin:minWinTime')}${isLeadVisitId ? '*' : ''}`}
                                    disabled={!isLeadVisitId || !canEdit}
                                    id="add-min-win-time"
                                    fullWidth
                                    type="number"
                                    value={state.minWinPeriod || ''}
                                    onKeyDown={onChange('minWinPeriod', true)}
                                    onChange={onChange('minWinPeriod', true)}
                                    placeholder={t('admin:enterMinWinTimeLabel')}
                                    helperText={
                                        <CharactersRemaining
                                            id="visit-min-win-time-help-text"
                                            error={errors.minWinPeriod}
                                            customText={t('common:enterNumericValue')}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.minWinPeriod}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type mb">
                                <TextField
                                    label={`${t('admin:maxWinTime')}${isLeadVisitId ? '*' : ''}`}
                                    disabled={!isLeadVisitId || !canEdit}
                                    id="add-max-win-time"
                                    fullWidth
                                    type="number"
                                    value={state.maxWinPeriod || ''}
                                    onKeyDown={onChange('maxWinPeriod', true)}
                                    onChange={onChange('maxWinPeriod', true)}
                                    placeholder={t('admin:enterMaxWinTimeLabel')}
                                    helperText={
                                        <CharactersRemaining
                                            id="visit-max-win-time-help-text"
                                            error={errors.maxWinPeriod}
                                            customText={t('common:enterNumericValue')}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.maxWinPeriod}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mb mr">
                                <SelectWithCategories
                                    showWarning={false}
                                    fieldSelect="minWinUnit"
                                    onChange={onChange}
                                    label={`${t('admin:minMaxUnit')}${isLeadVisitId ? '*' : ''}`}
                                    disabled={!isLeadVisitId || !canEdit}
                                    filterable={false}
                                    labelField="leadUnit"
                                    dataArray={unitOptions}
                                    customId="add-min-max-unit"
                                    currentState={state.minWinUnit}
                                    error={errors.minWinUnit}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <SelectWithCategories
                                    showWarning={false}
                                    fieldSelect="endDateUsage"
                                    onChange={onChange}
                                    label={`${t('admin:endDateUsage')}${isLeadVisitId ? '*' : ''}`}
                                    disabled={!isLeadVisitId || !canEdit}
                                    filterable={false}
                                    labelField="endDateUsage"
                                    dataArray={endDateUsage}
                                    customId="add-endDateUsage"
                                    currentState={state.endDateUsage}
                                    error={errors.endDateUsage}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type">
                                <ForecastEndDateField
                                    currentState={state.forecastEndDate}
                                    disabled={!isLeadVisitId || !canEdit}
                                    isRequired={isLeadVisitId}
                                    onChange={onChange}
                                    error={errors.forecastEndDate}
                                />
                            </FormControl>
                        </div>
                        {!!currentVisit && (
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type">
                                    <TextField
                                        id="edit-field-config-id"
                                        label={t('admin:configId')}
                                        onChange={onChange('configId')}
                                        fullWidth
                                        value={state.configId}
                                        size="small"
                                        slotProps={{
                                            input: {
                                                readOnly: !isSysAdmin,
                                                endAdornment: <CopyButton value={state.configId ?? ''} />,
                                            },
                                        }}
                                        helperText={
                                            <span style={{ color: '#ae0015' }} className="remaining-text">
                                                {errors.configId}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.configId}
                                    />
                                </FormControl>
                            </div>
                        )}
                        {currentVisit && <ModalUpdatedInfo item={currentVisit} />}
                    </div>
                </>
            )}
        </div>
    );
};

export default AddVisitForm;
